import { AIProvider, CallAnalysisResult, CallTranscriptPart, Company, CostMode, GeminiModel, Project } from '../types';
import { GeminiService } from './geminiService';
import { LocalLLMService } from './localLLMService';
import { OpenRouterService } from './openRouterService';
import { SettingsService } from './settingsService';

type AnalyzeCallInput = {
  project: Project;
  company: Company;
  transcript: CallTranscriptPart[];
  rawTranscript?: string;
};

const safeArray = (value: unknown) =>
  Array.from(
    new Set(
      (Array.isArray(value) ? value : [])
        .map((item) => String(item || '').trim())
        .filter(Boolean)
    )
  ).slice(0, 8);

const clampScore = (score: unknown) => {
  const value = Number(score);
  if (Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
};

const parseJsonFromText = (text: string) => {
  const trimmed = text.trim();
  if (!trimmed) return {};

  try {
    return JSON.parse(trimmed);
  } catch {
    // continue
  }

  const fenced = trimmed.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  if (fenced?.[1]) {
    try {
      return JSON.parse(fenced[1].trim());
    } catch {
      // continue
    }
  }

  const start = trimmed.indexOf('{');
  const end = trimmed.lastIndexOf('}');
  if (start !== -1 && end !== -1 && end > start) {
    try {
      return JSON.parse(trimmed.slice(start, end + 1));
    } catch {
      // continue
    }
  }

  return {};
};

const transcriptToText = (transcript: CallTranscriptPart[]) =>
  transcript
    .map((part) => `${part.speaker === 'contact' ? 'Contactpersoon' : 'BDAT'}: ${part.text}`)
    .join('\n');

const isMetaReasoningText = (text: string) => {
  const normalized = text.trim().toLowerCase();
  if (!normalized) return true;

  const patterns = [
    /^\*\*.+\*\*$/,
    /^\*\*[^\n]+\*\*[\s\S]*$/m,
    /\bformulating\b/,
    /\bhighlighting\b/,
    /\backnowledging\b/,
    /\bclarifying\b/,
    /\baddressing the observation\b/,
    /\bi've crafted\b/,
    /\bi'm focusing on\b/,
    /\bthe script dictated\b/,
    /\bstrategic approach\b/,
    /\blet's refocus\b/
  ];

  return patterns.some((pattern) => pattern.test(normalized));
};

const sanitizeTranscript = (transcript: CallTranscriptPart[]) =>
  transcript.filter((part) => !(part.speaker === 'agent' && isMetaReasoningText(part.text)));

const heuristicOutcome = (transcriptText: string) => {
  const normalized = transcriptText.toLowerCase();
  if (/(stuur|mail|afspraak|demo|terugbellen|offerte|interesse|laten we)/i.test(normalized)) {
    return 'positief_vervolg';
  }
  if (/(geen interesse|niet nodig|geen tijd|stop|nee dank|al voorzien)/i.test(normalized)) {
    return 'afwijzing';
  }
  return 'onduidelijk';
};

const buildFallback = (input: AnalyzeCallInput): CallAnalysisResult => {
  const transcriptText = transcriptToText(input.transcript);
  const outcome = heuristicOutcome(transcriptText);
  const objections = safeArray(
    transcriptText.match(/geen interesse|te duur|geen tijd|al voorzien|geen behoefte/gi) || []
  );
  const nextSteps = outcome === 'positief_vervolg'
    ? ['Plan een concreet vervolg of stuur dezelfde dag een opvolgmail.']
    : outcome === 'afwijzing'
      ? ['Log het bezwaar en kies alleen later opnieuw contact bij een nieuw haakje.']
      : ['Valideer in een vervolgcall wie beslist en of er een concrete behoefte is.'];

  return {
    mode: 'live-voice',
    transcript: input.transcript,
    rawTranscript: input.rawTranscript || transcriptText,
    summary: input.transcript.length
      ? `Live gesprek opgeslagen voor ${input.company.name}.`
      : `Gesprek opgeslagen, maar transcriptie voor ${input.company.name} was beperkt.`,
    sentiment: outcome === 'afwijzing' ? 'negative' : outcome === 'positief_vervolg' ? 'positive' : 'neutral',
    objections,
    nextSteps,
    score: outcome === 'positief_vervolg' ? 72 : outcome === 'afwijzing' ? 24 : 48,
    outcome,
    successfulAngles: outcome === 'positief_vervolg' ? ['Er was genoeg opening voor een concreet vervolg.'] : [],
    failedAngles: outcome === 'afwijzing' ? ['De pitch sloot nog niet scherp genoeg aan op de directe behoefte.'] : [],
    commonObjections: objections,
    coachingNotes: [
      'Blijf expliciet benoemen namens welke opdrachtgever je belt.',
      'Stuur sneller op kwalificatie en een concreet vervolg.'
    ],
    factualNotes: [
      input.company.website ? `Bedrijfswebsite in dossier: ${input.company.website}` : 'Geen extra feiten buiten het gesprek toegevoegd.'
    ]
  };
};

export class CallAnalysisService {
  static async analyzeLiveCall(input: AnalyzeCallInput): Promise<CallAnalysisResult> {
    const settings = SettingsService.getSettings();
    const sanitizedTranscript = sanitizeTranscript(input.transcript);
    const transcriptText = transcriptToText(sanitizedTranscript);
    if (!transcriptText.trim()) {
      return buildFallback({ ...input, transcript: sanitizedTranscript });
    }

    const systemPrompt = `
Je bent een Nederlandse sales call analyst voor BDAT.
Analyseer uitsluitend wat expliciet in het gesprek staat.
Verzin geen feiten, functies, eigenaarschap, intenties of uitkomsten.
Scheid feitelijke observaties van commerciële interpretatie.
Schrijf volledig in het Nederlands.
Geef alleen valide JSON terug.
`;

    const prompt = `
Analyseer dit outbound salesgesprek van BDAT.

PROJECT:
- Naam: ${input.project.name}
- Doel: ${input.project.description || 'Geen projectomschrijving beschikbaar'}

LEAD:
- Bedrijf: ${input.company.name}
- Contact: ${input.company.contactName || 'Onbekend'}

TRANSCRIPT:
${transcriptText}

Geef JSON terug met:
- summary: korte Nederlandse samenvatting van wat er besproken is
- sentiment: positive, neutral of negative
- objections: concrete bezwaren die echt in het gesprek voorkwamen
- nextSteps: concrete vervolgstappen op basis van het gesprek
- score: 0-100 op kansrijkheid van dit gesprek
- outcome: positief_vervolg, afwijzing, informatiegesprek of onduidelijk
- successfulAngles: wat in dit gesprek werkte
- failedAngles: wat niet werkte of verkeerd landde
- commonObjections: bezwaren die relevant zijn voor leerpatronen
- coachingNotes: korte Nederlandse verbeterpunten voor de bel-AI
- factualNotes: alleen feiten die expliciet uit het gesprek blijken
`;

    const schema = {
      type: 'object',
      properties: {
        summary: { type: 'string' },
        sentiment: { type: 'string' },
        objections: { type: 'array', items: { type: 'string' } },
        nextSteps: { type: 'array', items: { type: 'string' } },
        score: { type: 'number' },
        outcome: { type: 'string' },
        successfulAngles: { type: 'array', items: { type: 'string' } },
        failedAngles: { type: 'array', items: { type: 'string' } },
        commonObjections: { type: 'array', items: { type: 'string' } },
        coachingNotes: { type: 'array', items: { type: 'string' } },
        factualNotes: { type: 'array', items: { type: 'string' } }
      },
      required: [
        'summary',
        'sentiment',
        'objections',
        'nextSteps',
        'score',
        'outcome',
        'successfulAngles',
        'failedAngles',
        'commonObjections',
        'coachingNotes',
        'factualNotes'
      ]
    };

    try {
      let raw = '';
      const preferredModel = settings.researchModel || settings.defaultModel || GeminiModel.FLASH_3;

      if (settings.provider === AIProvider.OPENROUTER && settings.openRouterKey) {
        if (settings.costMode === CostMode.FREE_FIRST) {
          raw = (await OpenRouterService.generateTextFreeFirst(
            settings.openRouterKey,
            `${prompt}\n\nGeef alleen JSON terug.`,
            systemPrompt,
            preferredModel
          )).text;
        } else if (settings.costMode === CostMode.CHEAP_PAID_FALLBACK) {
          try {
            raw = (await OpenRouterService.generateTextFreeFirst(
              settings.openRouterKey,
              `${prompt}\n\nGeef alleen JSON terug.`,
              systemPrompt,
              preferredModel
            )).text;
          } catch (error) {
            console.warn('[CallAnalysis] Free OpenRouter failed, using paid auto fallback.', error);
            raw = await OpenRouterService.generateText(
              settings.openRouterKey,
              `${prompt}\n\nGeef alleen JSON terug.`,
              systemPrompt,
              OpenRouterService.PAID_AUTO_MODEL_ID
            );
          }
        } else {
          raw = await OpenRouterService.generateText(
            settings.openRouterKey,
            `${prompt}\n\nGeef alleen JSON terug.`,
            systemPrompt,
            preferredModel
          );
        }
      } else if (
        (settings.provider === AIProvider.OLLAMA || settings.provider === AIProvider.LMSTUDIO || settings.provider === AIProvider.LOCAL) &&
        settings.localUrl
      ) {
        raw = await LocalLLMService.generateText(
          settings.localUrl,
          `${prompt}\n\nGeef alleen JSON terug.`,
          systemPrompt,
          preferredModel,
          settings.localApiKey
        );
      } else if (settings.provider === AIProvider.GEMINI && settings.geminiKey) {
        const gemini = new GeminiService(settings.geminiKey);
        const response = await gemini.generateText(
          prompt,
          systemPrompt,
          preferredModel,
          false,
          schema,
          Math.min(settings.thinkingBudget || 0, 4096)
        );
        raw = response.text || '';
      } else {
        return buildFallback({ ...input, transcript: sanitizedTranscript });
      }

      const parsed = parseJsonFromText(raw);
      if (!parsed || typeof parsed !== 'object') {
        return buildFallback({ ...input, transcript: sanitizedTranscript });
      }

      return {
        mode: 'live-voice',
        transcript: sanitizedTranscript,
        rawTranscript: input.rawTranscript || transcriptText,
        summary: String((parsed as any).summary || `Analyse van gesprek met ${input.company.name}`),
        sentiment: ['positive', 'negative', 'neutral'].includes(String((parsed as any).sentiment))
          ? (parsed as any).sentiment
          : 'neutral',
        objections: safeArray((parsed as any).objections),
        nextSteps: safeArray((parsed as any).nextSteps),
        score: clampScore((parsed as any).score),
        outcome: String((parsed as any).outcome || heuristicOutcome(transcriptText)),
        successfulAngles: safeArray((parsed as any).successfulAngles),
        failedAngles: safeArray((parsed as any).failedAngles),
        commonObjections: safeArray((parsed as any).commonObjections),
        coachingNotes: safeArray((parsed as any).coachingNotes),
        factualNotes: safeArray((parsed as any).factualNotes)
      };
    } catch (error) {
      console.error('[CallAnalysis] Analyse mislukt, fallback actief:', error);
      return buildFallback({ ...input, transcript: sanitizedTranscript });
    }
  }
}
